import { useCallback, useContext, useState } from "react";
import { toast } from "react-hot-toast";
import {
  ApolloIntegrationApiKeyForm_ApolloIntegrationFragment,
  ChooseIntegration_PermissionsFragmentDoc,
  ChooseIntegration_UserAccountFragment,
  IntegrationEnum,
  IntegrationModal_UserAccountQuery,
  IntegrationModal_UserAccountQueryVariables,
} from "../../../__generated__/graphql";
import ChooseIntegration, {
  ChooseIntegration_UserAccount,
  ChooseIntegration_Permissions,
} from "./ChooseIntegration";
import ApolloIntegrationApiKeyForm, {
  ApolloIntegrationApiKeyForm_ApolloIntegration,
} from "./apollo/ApolloIntegrationApiKeyForm";
import IntegrationModalOAuth from "./oauth/IntegrationModalOAuth";
import { IntegrationModalPhases } from "../manageIntegrationButton/ManageIntegrationsButton";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import NavigationContext from "../../../contexts/NavigationContext";
import CiroModal from "../../shared/CiroModal";
import Loading from "../../shared/Loading";
import classNames from "classnames";
import { useFragment } from "../../../__generated__";

export const IntegrationModal_UserAccount = gql`
  query IntegrationModal_UserAccount {
    userAccount {
      pk
      org {
        organizationCrmIntegration {
          integration
        }
        apolloIntegration {
          ...ApolloIntegrationApiKeyForm_ApolloIntegration
        }
      }
      ...ChooseIntegration_UserAccount
    }
    ...ChooseIntegration_Permissions
  }
  ${ApolloIntegrationApiKeyForm_ApolloIntegration}
  ${ChooseIntegration_UserAccount}
  ${ChooseIntegration_Permissions}
`;

export function IntegrationModal() {
  const {
    integrationModalPhase,
    setIntegrationModalPhase,
    resetAvailableIntegrations,
  } = useContext(NavigationContext);

  const userAccountQuery = useQuery<
    IntegrationModal_UserAccountQuery,
    IntegrationModal_UserAccountQueryVariables
  >(IntegrationModal_UserAccount);

  const { data: userAccountData, loading: userAccountLoading } = userAccountQuery;

  const userAccount = userAccountData?.userAccount;

  const [targetIntegration, setTargetIntegration] =
    useState<IntegrationEnum | null>(null);

  const client = useApolloClient();

  const integrations = [] as IntegrationEnum[];
  if (userAccount?.org?.apolloIntegration) {
    integrations.push(IntegrationEnum.Apollo);
  }
  if (userAccount?.org?.organizationCrmIntegration) {
    integrations.push(
      userAccount?.org?.organizationCrmIntegration
        ?.integration as unknown as IntegrationEnum,
    );
  }

  const refreshUserAccount = useCallback(() => {
    client.cache.evict({ fieldName: "userAccount" });
  }, [client.cache]);

  const apolloIntegration = userAccount?.org?.apolloIntegration;

  const permissions = useFragment(
    ChooseIntegration_PermissionsFragmentDoc,
    userAccountData,
  );

  if (integrationModalPhase === IntegrationModalPhases.GET_STARTED) {
    return null;
  }

  if (userAccountLoading) {
    return (
      <CiroModal
        isOpen={true}
        size="SMALL"
        onClose={() => {
          resetAvailableIntegrations();
          setIntegrationModalPhase(IntegrationModalPhases.GET_STARTED);
        }}
      >
        <div className={classNames("flex", "justify-center", "items-center")}>
          <Loading size="SMALL" />
        </div>
      </CiroModal>
    );
  }

  return (
    <>
      {integrationModalPhase === IntegrationModalPhases.CHOOSE_INTEGRATION && (
        <ChooseIntegration
          connectedIntegrations={integrations}
          setIntegrationPhase={setIntegrationModalPhase}
          setTargetIntegration={setTargetIntegration}
          userAccount={userAccount as ChooseIntegration_UserAccountFragment}
          refetchUserAccount={refreshUserAccount}
          permissionsData={permissions}
        />
      )}
      {integrationModalPhase === IntegrationModalPhases.APOLLO_API_KEY && (
        <ApolloIntegrationApiKeyForm
          apolloIntegration={
            apolloIntegration as ApolloIntegrationApiKeyForm_ApolloIntegrationFragment
          }
          refetchUserAccount={refreshUserAccount}
          onSuccess={() => {
            toast.success("Successfully connected Apollo");
            setIntegrationModalPhase(IntegrationModalPhases.GET_STARTED);
            refreshUserAccount();
            resetAvailableIntegrations();
          }}
          onClose={() => {
            resetAvailableIntegrations();
            setIntegrationModalPhase(IntegrationModalPhases.GET_STARTED);
            refreshUserAccount();
          }}
        />
      )}
      {integrationModalPhase === IntegrationModalPhases.INITIATE_OAUTH && (
        <IntegrationModalOAuth
          integration={
            targetIntegration as
              | IntegrationEnum.Salesforce
              | IntegrationEnum.HubSpot
          }
          onClose={() => {
            resetAvailableIntegrations();
            setIntegrationModalPhase(IntegrationModalPhases.GET_STARTED);
          }}
          onSuccess={() => {
            toast.success(`Successfully connected ${targetIntegration}`);
            resetAvailableIntegrations();
            setIntegrationModalPhase(IntegrationModalPhases.GET_STARTED);
            refreshUserAccount();
          }}
          userAccountQuery={userAccountQuery}
        />
      )}
    </>
  );
}
